"use client"

import {useEffect, useRef, useState} from "react"
import "./AudioOutputLevel.scss"

interface AudioOutputLevelProps {
    audioElement?: HTMLAudioElement | HTMLVideoElement | null
    audioSource?: MediaElementAudioSourceNode | AudioNode | null | undefined
    minThreshold?: number
    className?: string
}

export const AudioOutputLevel = ({
                                     audioElement = null,
                                     audioSource = null,
                                     minThreshold = 15,
                                     className = "",
                                 }: AudioOutputLevelProps) => {
    const [outputLevel, setOutputLevel] = useState<number>(0)
    const audioContextRef = useRef<AudioContext | null>(null)
    const analyserRef = useRef<AnalyserNode | null>(null)
    const sourceNodeRef = useRef<MediaElementAudioSourceNode | AudioNode | null>(null)
    const animationRef = useRef<number | null>(null)

    useEffect(() => {
        // Clean up previous audio processing if it exists
        if (audioContextRef.current) {
            stopAudioProcessing()
        }

        // Set up new audio processing if audio element or source is provided
        if (audioElement || audioSource) {
            setupAudioProcessing()
        }

        // Cleanup on unmount or when audio element changes
        return () => {
            stopAudioProcessing()
        }
    }, [audioElement, audioSource])

    const setupAudioProcessing = () => {
        try {
            console.log('connecting analyzer to source node 1')
            // Create audio context
            if (!audioSource)
                return

            const audioContext = audioSource.context

            // Create analyzer
            const analyser = audioContext.createAnalyser()
            analyser.fftSize = 256
            analyser.smoothingTimeConstant = 0.3
            analyserRef.current = analyser

            // Connect the audio source to the analyzer
            // If an audio source node is provided directly
            sourceNodeRef.current = audioSource
            sourceNodeRef.current.connect(analyser)
            console.log('connecting analyzer to source node 2')


            // Start updating the audio level
            updateAudioLevel()
        } catch (err) {
            console.error("Error processing audio output:", err)
        }
    }

    const stopAudioProcessing = () => {
        // Stop the animation frame
        if (animationRef.current) {
            cancelAnimationFrame(animationRef.current)
            animationRef.current = null
        }

        // Clean up audio context
        if (sourceNodeRef.current) {
            sourceNodeRef.current.disconnect()
            sourceNodeRef.current = null
        }

        if (analyserRef.current) {
            analyserRef.current.disconnect()
            analyserRef.current = null
        }

        if (audioContextRef.current && audioContextRef.current.state !== "closed") {
            audioContextRef.current.close()
            audioContextRef.current = null
        }

        setOutputLevel(0)
    }

    const updateAudioLevel = () => {
        if (!analyserRef.current) return

        const bufferLength = analyserRef.current.frequencyBinCount
        const dataArray = new Uint8Array(bufferLength)

        const updateLevel = () => {
            if (!analyserRef.current) return

            // Get frequency data
            analyserRef.current.getByteFrequencyData(dataArray)

            // Calculate average level
            let sum = 0
            for (let i = 0; i < bufferLength; i++) {
                sum += dataArray[i]
            }
            const average = sum / bufferLength

            // Normalize to 0-100
            const normalizedLevel = Math.min(100, Math.max(0, (average / 255) * 100))
            setOutputLevel(normalizedLevel)

            animationRef.current = requestAnimationFrame(updateLevel)
        }

        animationRef.current = requestAnimationFrame(updateLevel)
    }

    return (
        <div className={`audio-output-level ${className}`}>
            <div className="audio-output-level-meter">
                <div className={`audio-output-level-fill ${outputLevel > 0 ? 'active' : ''}`} style={{width: `${outputLevel}%`}}/>
            </div>
        </div>
    )
}